import PricingFeaturesGroup from "@/components/pricingFeaturesGroup/pricingFeaturesGroup";
import styles from "./pricingTable.module.scss";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import CustomSpinner from "@/components/spinner/spinner";
import CustomPlan from "@/components/customPlan/customPlan";
import ToggleSwitch from "@/components/toggelSwicther/toggelSwicther";
import Head from "next/head";
import HeroGradientTextWithIcon from "@/components/heroGradientTextWithIcon/heroGradientTextWithIcon";
import PlanCardV2 from "@/components/planCardV2/planCardV2";
import { svgImages } from "@/lib/svgImages";
import ToggleSwitchCompare from "@/components/toggelSwictherCompare/toggelSwictherCompare";
import ButtonBgAnimate from "@/components/buttonBgAnimate/buttonBgAnimate";
import { useWordPressApi } from "@/hooks/useWordPressApi";

const PricingTable = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [pricingData, setPricingData] = useState(null);
  const [featuredFeatures, setFeaturedFeatures] = useState([]);
  const [plansData, setPlansData] = useState([]);
  const [currentUrl, setCurrentUrl] = useState("");
  const [domainUrl, setDomainUrl] = useState("");
  const [isAnnual, setIsAnnual] = useState(true);
  const { getPlansData } = useWordPressApi();
  useEffect(() => {
    if (window.location.href) {
      setCurrentUrl(window.location.href);
      setDomainUrl(window.location.hostname);
    }
  }, []);

  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoading(true);
        const response = await getPlansData();
        const data = await response.json();
        setPricingData(data);

        // Extract plans and their features from the actual API structure
        const plansArray = [];
        const featured = [];

        // Process plans from data.data.plans
        if (data?.data?.plans) {
          Object.values(data.data.plans).forEach((plan: any) => {
            if (plan.isPrivatePlan === true) {
              return;
            }

            // Extract plan data
            const planData = {
              title: plan.name,
              price: plan.monthlyPrice?.toString() || "0",
              annualPrice: plan.annualPrice?.toString() || "0",
              currencySymbol: "$",
              mostPopular:
                plan.name.toLowerCase() === "plus" || plan.popular || false,
              freeTrailValue: "7",
              buttonUrl: `${process.env.NEXT_PUBLIC_DASHBOARD_URL}/account/create`,
              buttonTitle: "Start for free",
              description: plan.description || "",
              planType: plan.planType || "",
              features: plan.features || [],
              keyFeatures: plan.key_features || [],
              order: plan.order || 999,
            };
            plansArray.push(planData);

            if (plan.key_features && plan.key_features.length > 0) {
              plan.key_features.forEach((keyFeature: any) => {
                let featureValue: any = "";

                if (typeof keyFeature.value === "boolean") {
                  featureValue = {
                    icon: (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: keyFeature.value
                            ? svgImages.qr_true
                            : svgImages.qr_false,
                        }}
                      />
                    ),
                    disabled: !keyFeature.value,
                  };
                } else if (typeof keyFeature.value === "number") {
                  featureValue = (
                    <>
                      <span
                        className={`${styles["featureValueIcon"]}`}
                        dangerouslySetInnerHTML={{ __html: svgImages.qr_true }}
                      />{" "}
                      {keyFeature.value.toString()}
                    </>
                  );
                } else if (typeof keyFeature.value === "string") {
                  featureValue = (
                    <>
                      <span
                        className={`${styles["featureValueIcon"]}`}
                        dangerouslySetInnerHTML={{ __html: svgImages.qr_true }}
                      />{" "}
                      {keyFeature.value.charAt(0).toUpperCase() +
                        keyFeature.value.slice(1)}
                    </>
                  );
                }

                // Check if this feature already exists in featured array
                const existingFeature = featured.find(
                  (f) => f.title === keyFeature.name,
                );
                if (existingFeature) {
                  // Add this plan's value to the existing feature
                  existingFeature.value[plan.name] = {
                    value: featureValue,
                    disabled:
                      typeof keyFeature.value === "boolean"
                        ? !keyFeature.value
                        : false,
                  };
                } else {
                  featured.push({
                    title: keyFeature.name,
                    value: {
                      [plan.name]: {
                        value: featureValue,
                        disabled:
                          typeof keyFeature.value === "boolean"
                            ? !keyFeature.value
                            : false,
                      },
                    },
                    tooltip: "",
                  });
                }
              });
            }
          });
        }

        // Sort plans by order value
        plansArray.sort((a, b) => a.order - b.order);

        setPlansData(plansArray);
        setFeaturedFeatures(featured);
      } catch (error) {
        console.error("Failed to fetch pricing data:", error);
        // Set fallback data structure
        setPricingData({
          data: {
            plans: {},
          },
        });
        setFeaturedFeatures([]);
        setPlansData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPricingData();
  }, []);

  function isMobile() {
    const regex =
      /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    return regex.test(navigator.userAgent);
  }

  const baseUrl = `https://${domainUrl || "qrhub.io"}`;
  const defaultOgImage = `${baseUrl}/images/featured_images/qrhub-featured.jpg`;
  const defaultOgUrl = currentUrl || `${baseUrl}${router.asPath || ""}`;

  return (
    <>
      <Head>
        <title>QRHUB Pricing</title>
        <meta
          name="description"
          content="Empower your business with tailored QR code Pricing"
        ></meta>
        <meta property="og:title" content="QRHUB Pricing"></meta>
        <meta
          property="og:description"
          content="Empower your business with tailored QR code Pricing"
        ></meta>
        <meta property="og:image" content={defaultOgImage}></meta>
        <meta property="og:url" content={defaultOgUrl}></meta>
      </Head>
      {loading ? (
        <>
          <CustomSpinner />
        </>
      ) : (
        <>
          <div className={`${styles["pricingHero"]}`}>
            <HeroGradientTextWithIcon
              subtitle=""
              title="Plans & Pricing"
              description="Engage your audience using our trackable micro-services  Generate Pages, QR Codes, and links."
              image={null}
              buttonTitle=""
              buttonUrl=""
              isProduct={true}
              isSolution={false}
              isStatic={false}
              isPricing={true}
            />
          </div>
          <div className="custom-container">
            <div className="inner">
              <div className={`${styles["pricingHeroContainer"]}`}>
                <ToggleSwitch
                  isChecked={isAnnual}
                  setIsChecked={setIsAnnual}
                  firstVal={`Annually`}
                  secondVal={`Monthly`}
                  pricingPage={true}
                  secondIcon={false}
                />
                <div className={`${styles["planCards"]}`}>
                  {plansData.map((plan, index) => (
                    <PlanCardV2
                      key={plan.title}
                      compareFeatures={false}
                      title={plan.title}
                      price={
                        plan.title.toLowerCase() === "free"
                          ? "0"
                          : isAnnual
                            ? Math.round(
                                parseFloat(plan.annualPrice) / 12,
                              ).toString()
                            : plan.price
                      }
                      annualCost={
                        plan.title.toLowerCase() === "free"
                          ? "0"
                          : plan.annualPrice
                      }
                      buttonUrl={plan.buttonUrl}
                      buttonTitle={plan.buttonTitle}
                      mostPopular={plan.mostPopular}
                      isAnnual={isAnnual}
                      currencySymbol={plan.currencySymbol}
                      freeTrailValue={plan.freeTrailValue}
                      featureList={featuredFeatures}
                      hideFeatureList={false}
                      planName={plan.title}
                    />
                  ))}
                </div>

                <div className={`${styles["trial-banner"]}`}>
                  <div className={`${styles["trial-banner-content"]}`}>
                    <h2 className="font-size-32">
                      Experience QRHub Without Commitment
                    </h2>
                    <p>
                      We believe in transparency. That’s why you can try every
                      feature before choosing a plan.
                    </p>
                    <ButtonBgAnimate
                      title="Try Everything for 7 Days"
                      url={process.env.NEXT_PUBLIC_DASHBOARD_URL}
                      target={"_blank"}
                      outline={false}
                      light={true}
                    />
                  </div>
                </div>

                <CustomPlan />
              </div>
            </div>
            <div className="d-none-xl" style={{ marginTop: "40px" }}>
              <h2 className="font-size-32">Compare Plans & Features</h2>
            </div>
          </div>

          <div className="custom-container">
            <div className="inner">
              <div className={`${styles["packages"]}`}>
                <div
                  className={`${styles["planCards"]} ${styles["compareFeatures"]}`}
                >
                  <div className={`${styles["compareTitle"]}`}>
                    <h2 className="font-size-32">Compare Plans & Features</h2>
                    <ToggleSwitchCompare
                      isChecked={isAnnual}
                      setIsChecked={setIsAnnual}
                      pricingPage={true}
                    />
                  </div>
                  {plansData.map((plan, index) => (
                    <PlanCardV2
                      key={`compare-${plan.title}`}
                      compareFeatures={true}
                      title={plan.title}
                      price={
                        plan.title.toLowerCase() === "free"
                          ? "0"
                          : isAnnual
                            ? Math.round(
                                parseFloat(plan.annualPrice) / 12,
                              ).toString()
                            : plan.price
                      }
                      annualCost={
                        plan.title.toLowerCase() === "free"
                          ? "0"
                          : plan.annualPrice
                      }
                      buttonUrl={plan.buttonUrl}
                      buttonTitle={plan.buttonTitle}
                      mostPopular={plan.mostPopular}
                      isAnnual={isAnnual}
                      currencySymbol={plan.currencySymbol}
                      freeTrailValue={plan.freeTrailValue}
                      planName={plan.title}
                      featureList={featuredFeatures}
                      hideFeatureList={true}
                    />
                  ))}
                </div>

                <div
                  className={`${styles["pricingFeaturesGroupContainer"]}`}
                  key="features-container"
                >
                  {(() => {
                    // Organize features by category from the existing pricing data
                    const featuresByCategory: { [key: string]: any[] } = {};

                    if (pricingData?.data?.plans) {
                      // Iterate through each plan (plans is an object, not an array)
                      Object.values(pricingData.data.plans).forEach(
                        (plan: any) => {
                          // Skip private plans completely
                          if (plan.isPrivatePlan === true) {
                            return;
                          }

                          if (plan.features && Array.isArray(plan.features)) {
                            plan.features.forEach((feature: any) => {
                              const category = feature.category || "General";

                              if (!featuresByCategory[category]) {
                                featuresByCategory[category] = [];
                              }

                              // Check if this feature already exists in the category
                              const existingFeature = featuresByCategory[
                                category
                              ].find(
                                (f: any) => f.feature_title === feature.name,
                              );

                              if (existingFeature) {
                                // Add this plan's value to the existing feature
                                // Ensure existingFeature has feature_key from API
                                if (
                                  !existingFeature.feature_key &&
                                  feature.key
                                ) {
                                  existingFeature.feature_key = feature.key;
                                }
                                // Ensure existingFeature has isStatic from API
                                if (
                                  existingFeature.isStatic === undefined &&
                                  feature.isStatic !== undefined
                                ) {
                                  existingFeature.isStatic = feature.isStatic;
                                }
                                existingFeature.plans[0].plan.push({
                                  plan_title: plan.name,
                                  type_of_value:
                                    typeof feature.value === "boolean"
                                      ? "boolean"
                                      : typeof feature.value === "string"
                                        ? "text"
                                        : Array.isArray(feature.value)
                                          ? "format"
                                          : "text",
                                  boolean_value:
                                    typeof feature.value === "boolean"
                                      ? feature.value
                                      : undefined,
                                  text_value:
                                    typeof feature.value === "string" ||
                                    typeof feature.value === "number"
                                      ? feature.value?.toString()
                                      : undefined,
                                  format_value: Array.isArray(feature.value)
                                    ? feature.value
                                    : undefined,
                                });
                              } else {
                                // Create new feature entry
                                const transformedFeature = {
                                  feature_title: feature.name,
                                  feature_key: feature.key,
                                  feature_tooltip: feature.description || "",
                                  isStatic: feature.isStatic || false,
                                  parent_feature: feature.parent_feature || "",
                                  plans: [
                                    {
                                      plan: [
                                        {
                                          plan_title: plan.name,
                                          type_of_value:
                                            typeof feature.value === "boolean"
                                              ? "boolean"
                                              : typeof feature.value ===
                                                  "string"
                                                ? "text"
                                                : Array.isArray(feature.value)
                                                  ? "format"
                                                  : "text",
                                          boolean_value:
                                            typeof feature.value === "boolean"
                                              ? feature.value
                                              : undefined,
                                          text_value:
                                            typeof feature.value === "string" ||
                                            typeof feature.value === "number"
                                              ? feature.value?.toString()
                                              : undefined,
                                          format_value: Array.isArray(
                                            feature.value,
                                          )
                                            ? feature.value
                                            : undefined,
                                        },
                                      ],
                                    },
                                  ],
                                };

                                featuresByCategory[category].push(
                                  transformedFeature,
                                );
                              }
                            });
                          }
                        },
                      );
                    }

                    // Convert to array format for rendering
                    const organizedFeatures = Object.entries(
                      featuresByCategory,
                    ).map(([category, features]) => ({
                      category,
                      features,
                    }));

                    // Sort features within each category to match dynamic plan order
                    const planOrder = plansData.map((plan) => plan.title);

                    organizedFeatures.forEach((group) => {
                      group.features.forEach((feature) => {
                        if (feature.plans[0] && feature.plans[0].plan) {
                          // Sort the plans array to match the dynamic order
                          feature.plans[0].plan.sort((a, b) => {
                            const aIndex = planOrder.indexOf(a.plan_title);
                            const bIndex = planOrder.indexOf(b.plan_title);
                            return aIndex - bIndex;
                          });
                        }
                      });
                    });

                    if (organizedFeatures.length > 0) {
                      return organizedFeatures.map((group, index) => (
                        <PricingFeaturesGroup
                          key={`${group.category}-${index}`}
                          mainTitle={group.category}
                          groupItems={group.features}
                        />
                      ));
                    } else {
                      return (
                        <div>
                          <h3>No features data available</h3>
                          <p>
                            Please check the API connection or try again later.
                          </p>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PricingTable;
